import { useApolloClient } from '@apollo/client';
import { useEffect, useState, useCallback } from 'react';
import { useMatch } from 'react-router-dom';
import { useMap } from 'react-use';
import { useRecoilValue } from 'recoil';
import { percomaticLoadedState } from '../../../atoms';
import { transItemProductState } from '../../../Product/atoms';
import { BreadCrumbItemProps } from '../../components/BreadCrumb/BreadCrumbItem';
import {
	BreadcrumbProductGroupQuery,
	BreadcrumbProductGroupDocument,
	BreadcrumbProductGroupQueryVariables
} from '../../graphql';

const defaultBreadcrumb: BreadCrumbItemProps = {
	label: 'Menu',
	url: '/',
	active: true
};

type BreadCrumb = { props: BreadCrumbItemProps; index: number };

type BreadCrumbMap = { [x: string]: BreadCrumb };

export const useBreadcrumb = (): BreadCrumbItemProps[] => {
	const [matchMemo, setMatchMemo] = useState<ReturnType<
		typeof useMatch
	> | null>(null);
	const { query } = useApolloClient();
	const [crumbs, { reset, set }] = useMap<BreadCrumbMap>();
	const match = useMatch('/menu/*');
	const [hasProduct, setHasProduct] = useState(false);
	const [productCrumb, setProductCrumb] = useState<
		BreadCrumbItemProps | undefined
	>();

	const percomaticLoaded = useRecoilValue(percomaticLoadedState);
	const product = useRecoilValue(transItemProductState);

	const runQuery = useCallback(
		(id: string, index: number, url: string, active: boolean) => {
			query<
				BreadcrumbProductGroupQuery,
				BreadcrumbProductGroupQueryVariables
			>({
				query: BreadcrumbProductGroupDocument,
				variables: { id }
			})
				.then(({ data }) => {
					set(id, {
						index,
						props: {
							label: data.onlineProductGroup.name,
							url: active ? undefined : url,
							active
						}
					});
				})
				.catch(err => {
					console.error(err);
				});
		},
		[query, set]
	);

	useEffect(() => {
		if (JSON.stringify(match) !== JSON.stringify(matchMemo)) {
			// console.debug('updating match', JSON.stringify(match));

			setMatchMemo(match);
		}
	}, [match, matchMemo]);

	useEffect(() => {
		if (hasProduct) {
			if (percomaticLoaded) {
				setProductCrumb({
					label: product?.name ?? 'Loading...',
					active: true
				});
			} else {
				setProductCrumb({
					label: 'Loading...',
					active: true
				});
			}
		} else {
			setProductCrumb(undefined);
		}
	}, [hasProduct, percomaticLoaded, product]);

	useEffect(() => {
		// console.debug('useBreadcrumb: useEffect');

		if (matchMemo) {
			reset();

			const {
				params: { '*': wildcard }
			} = matchMemo;

			if (wildcard && wildcard !== '') {
				const tokens = wildcard.split('/');

				const productIndex = tokens.indexOf('product');

				setHasProduct(productIndex > -1);

				const groupIds: string[] =
					productIndex === -1
						? tokens
						: tokens.slice(0, productIndex);

				groupIds.forEach((id, index) => {
					const url = groupIds.reduce((acc, id, i) => {
						if (i <= index) {
							return `${acc}/${id}`;
						}

						return acc;
					}, '/menu');

					const active = index === tokens.length - 1;

					set(id, {
						index,
						props: {
							label: 'Loading...',
							url: active ? undefined : url,
							active
						}
					});

					runQuery(id, index, url, active);
				});
			} else {
				setHasProduct(false);
			}
		} else {
			setHasProduct(false);
		}
	}, [matchMemo, reset, set, runQuery]);

	const crumbValues = Object.values(crumbs);

	if (match === null) {
		return [{ label: 'Menu', url: '/', active: false }];
	}

	return [
		defaultBreadcrumb,
		...crumbValues
			.sort((a, b) => a.index - b.index)
			.map(({ props }) => props),
		...(productCrumb ? [productCrumb] : [])
	];
};
